import { supabase } from '@/lib/supabase';
import { PropBot, PropBotDetails } from '@/types/prop-bots';
import { v4 as uuidv4 } from 'uuid';
import { createShadowBot, deleteShadowBot } from '../shadow-bot-service';

/**
 * Fetch all prop bots with pagination, search, and filtering
 */
export const fetchAdminPropBots = async ({
  page = 1,
  pageSize = 10,
  searchQuery = '',
  sortBy = 'created_date',
  sortOrder = 'desc',
  status = 'all',
}: {
  page?: number;
  pageSize?: number;
  searchQuery?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  status?: string;
}) => {
  try {
    // Calculate pagination
    const start = (page - 1) * pageSize;
    const end = start + pageSize - 1;

    // Start building the query
    let query = supabase
      .from('bots')
      .select(`
        id,
        name,
        description,
        status,
        color_scheme,
        type,
        created_date,
        propDetails:bot_prop_details(*)
      `, { count: 'exact' })
      .eq('type', 'prop_bot');

    console.log('Building query for prop bots with params:', { page, pageSize, searchQuery, sortBy, sortOrder, status });

    // Add search if provided
    if (searchQuery) {
      query = query.or(`name.ilike.%${searchQuery}%, description.ilike.%${searchQuery}%`);
    }

    // Add status filter if provided and not 'all'
    if (status && status !== 'all') {
      query = query.eq('status', status);
    }

    // Add sorting
    query = query.order(sortBy, { ascending: sortOrder === 'asc' });

    // Add pagination
    query = query.range(start, end);

    // Execute query
    const { data, error, count } = await query;

    if (error) {
      console.error('Error fetching admin prop bots:', error);
      throw error;
    }

    console.log('Fetched prop bots:', data);
    
    // Check if propDetails is being correctly returned
    if (data && data.length > 0) {
      console.log('Sample propDetails for first bot:', data[0].propDetails);
    }

    // Cast to PropBot type with proper type assertion
    const typedPropBots = data as unknown as PropBot[];

    return {
      bots: typedPropBots,
      totalCount: count || 0,
      currentPage: page,
      pageSize,
      totalPages: Math.ceil((count || 0) / pageSize),
    };
  } catch (error) {
    console.error('Error fetching admin prop bots:', error);
    throw error;
  }
};

/**
 * Fetch a single prop bot by ID
 */
export const fetchAdminPropBotById = async (botId: string) => {
  try {
    const { data: bot, error: botError } = await supabase
      .from('bots')
      .select('*')
      .eq('id', botId)
      .eq('type', 'prop_bot')
      .single();

    if (botError) throw botError;

    // Get prop details
    const { data: propDetails, error: detailsError } = await supabase
      .from('bot_prop_details')
      .select('*')
      .eq('bot_id', botId)
      .single();

    if (detailsError && !detailsError.message.includes('No rows found')) {
      throw detailsError;
    }

    // Log the data to debug
    console.log('Fetched prop bot by ID:', bot);
    console.log('Fetched prop details:', propDetails);

    // For consistency with the PropBotForm component, ensure the propDetails is properly formatted
    const formattedDetails = propDetails ? {
      ...propDetails,
      // Ensure array fields are properly parsed from string if needed
      markets: Array.isArray(propDetails.markets) ? propDetails.markets : 
              (typeof propDetails.markets === 'string' && propDetails.markets !== '[]' ? 
                JSON.parse(propDetails.markets) : []),
      pairs: Array.isArray(propDetails.pairs) ? propDetails.pairs : 
             (typeof propDetails.pairs === 'string' && propDetails.pairs !== '[]' ? 
              JSON.parse(propDetails.pairs) : []),
      account_sizes: Array.isArray(propDetails.account_sizes) ? propDetails.account_sizes : 
                    (typeof propDetails.account_sizes === 'string' && propDetails.account_sizes ? 
                      JSON.parse(propDetails.account_sizes) : []),
      features: Array.isArray(propDetails.features) ? propDetails.features : 
               (typeof propDetails.features === 'string' && propDetails.features ? 
                JSON.parse(propDetails.features) : []),
      requirements: Array.isArray(propDetails.requirements) ? propDetails.requirements : 
                   (typeof propDetails.requirements === 'string' && propDetails.requirements ? 
                    JSON.parse(propDetails.requirements) : []),
      challenge_rules: Array.isArray(propDetails.challenge_rules) ? propDetails.challenge_rules : 
                      (typeof propDetails.challenge_rules === 'string' && propDetails.challenge_rules ? 
                       JSON.parse(propDetails.challenge_rules) : []),
    } : null;

    return {
      ...bot,
      propDetails: formattedDetails,
    } as PropBot;
  } catch (error) {
    console.error('Error fetching prop bot by ID:', error);
    throw error;
  }
};

/**
 * Create a new prop trading bot
 */
export const createPropBot = async (botData: Partial<PropBot> & { originalLanguage?: string }) => {
  try {
    // Extract prop details and original language from the bot data
    const { propDetails, originalLanguage, ...botInfo } = botData;
    
    // Make sure we set the type to prop_bot and generate required tokens
    const botPayload = {
      ...botInfo,
      type: 'prop_bot',
      signal_token: uuidv4(),
      reverse_signal_token: uuidv4(),
      webhook_url: 'https://api.tradebot365.com/api/webhook/order',
      created_date: new Date().toISOString(),
    };
    
    // Insert into bots table
    const { data: newBot, error: botError } = await supabase
      .from('bots')
      .insert([botPayload])
      .select()
      .single();
      
    if (botError) throw botError;
    
    // Create shadow bot after successful creation
    try {
      await createShadowBot(newBot);
    } catch (shadowError) {
      console.error('Error creating shadow bot for prop bot:', shadowError);
      // Don't fail the main bot creation if shadow bot creation fails
    }
    
    // If we have prop details, add them
    if (propDetails) {
      const propDetailsPayload = {
        ...propDetails,
        bot_id: newBot.id,
      };
      
      // Check if prop details already exist (prevent duplicates)
      const { data: existingDetails } = await supabase
        .from('bot_prop_details')
        .select('id')
        .eq('bot_id', newBot.id)
        .maybeSingle();
      
      let newPropDetails;
      
      if (existingDetails) {
        console.warn('Prop details already exist for bot:', newBot.id, '- updating instead');
        // Update existing instead of insert
        const { data: updatedDetails, error: updateError } = await supabase
          .from('bot_prop_details')
          .update(propDetailsPayload)
          .eq('bot_id', newBot.id)
          .select()
          .single();
        
        if (updateError) throw updateError;
        newPropDetails = updatedDetails;
      } else {
        // Insert new prop details
        const { data: insertedDetails, error: insertError } = await supabase
          .from('bot_prop_details')
          .insert([propDetailsPayload])
          .select()
          .single();
        
        if (insertError) {
          // Check if it's a unique constraint violation
          if (insertError.code === '23505') {
            console.warn('Duplicate detected during insert, attempting upsert');
            // Fallback to upsert
            const { data: upsertedDetails, error: upsertError } = await supabase
              .from('bot_prop_details')
              .upsert(propDetailsPayload, { onConflict: 'bot_id' })
              .select()
              .single();
            
            if (upsertError) throw upsertError;
            newPropDetails = upsertedDetails;
          } else {
            throw insertError;
          }
        } else {
          newPropDetails = insertedDetails;
        }
      }

      // Also save to i18n table for the original language
      const originalLang = originalLanguage || 'en';
      const i18nContent = {
        bot_id: newBot.id,
        language_code: originalLang,
        description: botData.description,
        features: propDetails.features,
        requirements: propDetails.requirements,
        challenge_rules: propDetails.challenge_rules,
        is_original_language: true,
        is_ai_translated: false,
      };

      const { error: i18nError } = await supabase
        .from('bot_prop_details_i18n')
        .insert([i18nContent]);

      if (i18nError) {
        console.error('Error saving original language content:', i18nError);
        // Don't fail the main creation if i18n save fails
      }
      
      return {
        ...newBot,
        propDetails: newPropDetails,
      } as PropBot;
    }
    
    return newBot as PropBot;
  } catch (error) {
    console.error('Error creating prop bot:', error);
    throw error;
  }
};

/**
 * Update an existing prop trading bot
 */
export const updatePropBot = async (botId: string, botData: Partial<PropBot> & { originalLanguage?: string }) => {
  try {
    // Extract prop details and original language from the bot data
    const { propDetails, originalLanguage, ...botInfo } = botData;
    
    // Remove tokens from botInfo to prevent accidentally overwriting them
    const { signal_token, reverse_signal_token, ...safeUpdateData } = botInfo;
    
    // Update bots table (excluding tokens to preserve existing ones)
    const { data: updatedBot, error: botError } = await supabase
      .from('bots')
      .update(safeUpdateData)
      .eq('id', botId)
      .eq('type', 'prop_bot')
      .select()
      .single();
      
    if (botError) throw botError;
    
    // If we have prop details, update them
    if (propDetails) {
      // Check if prop details already exist
      const { data: existingDetails } = await supabase
        .from('bot_prop_details')
        .select()
        .eq('bot_id', botId)
        .single();
      
      let updatedPropDetails;
      
      if (existingDetails) {
        // Update existing details
        const { data: propDetailsData, error: propDetailsError } = await supabase
          .from('bot_prop_details')
          .update(propDetails)
          .eq('bot_id', botId)
          .select()
          .single();
          
        if (propDetailsError) throw propDetailsError;
        updatedPropDetails = propDetailsData;
      } else {
        // Insert new details
        const propDetailsPayload = {
          ...propDetails,
          bot_id: botId,
        };
        
        const { data: propDetailsData, error: propDetailsError } = await supabase
          .from('bot_prop_details')
          .insert([propDetailsPayload])
          .select()
          .single();
          
        if (propDetailsError) throw propDetailsError;
        updatedPropDetails = propDetailsData;
      }

      // Update i18n table for the original language
      if (originalLanguage) {
        const i18nContent = {
          description: botData.description,
          features: propDetails.features,
          requirements: propDetails.requirements,
          challenge_rules: propDetails.challenge_rules,
          is_original_language: true,
          is_ai_translated: false,
        };

        const { error: i18nError } = await supabase
          .from('bot_prop_details_i18n')
          .upsert({
            bot_id: botId,
            language_code: originalLanguage,
            ...i18nContent,
          });

        if (i18nError) {
          console.error('Error updating original language content:', i18nError);
          // Don't fail the main update if i18n update fails
        }
      }
      
      return {
        ...updatedBot,
        propDetails: updatedPropDetails,
      } as PropBot;
    }
    
    return updatedBot as PropBot;
  } catch (error) {
    console.error('Error updating prop bot:', error);
    throw error;
  }
};

/**
 * Delete a prop trading bot
 */
export const deletePropBot = async (botId: string) => {
  try {
    // First delete shadow bot
    try {
      await deleteShadowBot(botId);
    } catch (shadowError) {
      console.error('Error deleting shadow bot for prop bot:', shadowError);
      // Don't fail the main bot deletion if shadow bot deletion fails
    }
    
    // First delete any prop details
    const { error: detailsError } = await supabase
      .from('bot_prop_details')
      .delete()
      .eq('bot_id', botId);
    
    // Ignore not found errors for details
    if (detailsError && !detailsError.message.includes('No rows found')) {
      throw detailsError;
    }
    
    // Then delete the bot
    const { error: botError } = await supabase
      .from('bots')
      .delete()
      .eq('id', botId)
      .eq('type', 'prop_bot');
      
    if (botError) throw botError;
    
    return { success: true };
  } catch (error) {
    console.error('Error deleting prop bot:', error);
    throw error;
  }
};
